import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface BaselineInputsProps {
  path: "orthopedic" | "gi";
  values: {
    painLevel?: number;
    mobilityRating?: number;
    duration?: string;
    dailyImpact?: string;
    giFrequency?: string;
    giSeverity?: number;
    giDuration?: string;
  };
  onChange: (key: string, value: string | number) => void;
}

export function BaselineInputs({ path, values, onChange }: BaselineInputsProps) {
  if (path === "orthopedic") {
    return (
      <div className="space-y-8">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label htmlFor="pain-level" className="text-base font-medium">
              Pain Level
            </Label>
            <span className="text-2xl font-semibold text-teal-700">
              {values.painLevel ?? 0}
            </span>
          </div>
          <Slider
            id="pain-level"
            min={0}
            max={10}
            step={1}
            value={[values.painLevel ?? 0]}
            onValueChange={(val) => onChange("painLevel", val[0])}
            className="[&_.bg-primary]:bg-teal-700"
            data-testid="slider-pain-level"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>No pain</span>
            <span>Worst pain</span>
          </div>
        </div>

        <div className="space-y-3">
          <Label className="text-base font-medium">Mobility / Stiffness Rating</Label>
          <RadioGroup
            value={values.mobilityRating?.toString() ?? "3"}
            onValueChange={(val) => onChange("mobilityRating", Number(val))}
          >
            {[
              { value: "1", label: "Very stiff" },
              { value: "2", label: "Stiff" },
              { value: "3", label: "Moderate" },
              { value: "4", label: "Good" },
              { value: "5", label: "Full range" },
            ].map((option) => (
              <div
                key={option.value}
                className="flex items-center space-x-2"
                data-testid={`radio-mobility-${option.value}`}
              >
                <RadioGroupItem value={option.value} id={`mobility-${option.value}`} />
                <Label htmlFor={`mobility-${option.value}`} className="cursor-pointer">
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <div className="space-y-3">
          <Label htmlFor="duration" className="text-base font-medium">
            Duration
          </Label>
          <Select
            value={values.duration ?? ""}
            onValueChange={(val) => onChange("duration", val)}
          >
            <SelectTrigger id="duration" data-testid="select-duration">
              <SelectValue placeholder="Select duration" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="less-1-week">Less than 1 week</SelectItem>
              <SelectItem value="1-4-weeks">1–4 weeks</SelectItem>
              <SelectItem value="1-3-months">1–3 months</SelectItem>
              <SelectItem value="3-6-months">3–6 months</SelectItem>
              <SelectItem value="6-plus-months">6+ months</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          <Label className="text-base font-medium">Daily Activity Impact</Label>
          <RadioGroup
            value={values.dailyImpact ?? ""}
            onValueChange={(val) => onChange("dailyImpact", val)}
          >
            {[
              { value: "none", label: "None" },
              { value: "mild", label: "Mild" },
              { value: "moderate", label: "Moderate" },
              { value: "severe", label: "Severe" },
            ].map((option) => (
              <div
                key={option.value}
                className="flex items-center space-x-2"
                data-testid={`radio-impact-${option.value}`}
              >
                <RadioGroupItem value={option.value} id={`impact-${option.value}`} />
                <Label htmlFor={`impact-${option.value}`} className="cursor-pointer">
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      </div>
    );
  }

  // GI baseline
  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <Label htmlFor="gi-freq-baseline" className="text-base font-medium">
          Symptom Frequency
        </Label>
        <Select
          value={values.giFrequency ?? ""}
          onValueChange={(val) => onChange("giFrequency", val)}
        >
          <SelectTrigger id="gi-freq-baseline" data-testid="select-gi-freq-baseline">
            <SelectValue placeholder="Select frequency" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="occasionally">Occasionally</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="several-times-week">Several times a week</SelectItem>
            <SelectItem value="daily">Daily</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label htmlFor="gi-severity" className="text-base font-medium">
            Severity
          </Label>
          <span className="text-2xl font-semibold text-teal-700">
            {values.giSeverity ?? 0}
          </span>
        </div>
        <Slider
          id="gi-severity"
          min={0}
          max={10}
          step={1}
          value={[values.giSeverity ?? 0]}
          onValueChange={(val) => onChange("giSeverity", val[0])}
          className="[&_.bg-primary]:bg-teal-700"
          data-testid="slider-gi-severity"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Minimal</span>
          <span>Severe</span>
        </div>
      </div>

      <div className="space-y-3">
        <Label htmlFor="gi-dur-baseline" className="text-base font-medium">
          Duration
        </Label>
        <Select
          value={values.giDuration ?? ""}
          onValueChange={(val) => onChange("giDuration", val)}
        >
          <SelectTrigger id="gi-dur-baseline" data-testid="select-gi-dur-baseline">
            <SelectValue placeholder="Select duration" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="less-2-weeks">Less than 2 weeks</SelectItem>
            <SelectItem value="2-4-weeks">2–4 weeks</SelectItem>
            <SelectItem value="1-3-months">1–3 months</SelectItem>
            <SelectItem value="3-plus-months">More than 3 months</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
